/**
 * Client-side game constants for lobby UI (CreateGameDialog, GameCard)
 * Mirrors server-side constants from server/src/lib/game-logic/constants.ts
 */

type Region = 'crown' | 'span' | 'roots' | 'deep';
type Difficulty = 'grounded' | 'classic' | 'heroic';
type GameLanguage = 'en' | 'fi';
type AdventureTheme =
  | 'dungeon_crawling'
  | 'underworld'
  | 'city_guard'
  | 'intrigue'
  | 'mystery'
  | 'monster_hunting';

export const DIFFICULTIES: Record<Difficulty, {
  name: string;
  description: string;
  vitals: number;
  resolve: number;
  shadowMultiplier: number;
}> = {
  grounded: {
    name: 'Grounded',
    description: 'A brutal, unforgiving world. Death comes easily.',
    vitals: 8,
    resolve: 10,
    shadowMultiplier: 1.5,
  },
  classic: {
    name: 'Classic',
    description: 'The standard experience. Challenging but fair.',
    vitals: 10,
    resolve: 12,
    shadowMultiplier: 1,
  },
  heroic: {
    name: 'Heroic',
    description: 'A power fantasy. Heroes can withstand more.',
    vitals: 12,
    resolve: 15,
    shadowMultiplier: 0.75,
  },
} as const;

export const THEMES = DIFFICULTIES;

export const REGIONS: Record<Region, {
  name: string;
  faction: string;
  description: string;
  vibe: string;
}> = {
  crown: {
    name: 'The Crown',
    faction: 'The Steel Watch',
    description: 'The seat of power. Gleaming spires, noble estates, government halls, and temples.',
    vibe: 'Oppressive order, gleaming architecture, political intrigue.',
  },
  span: {
    name: 'The Span',
    faction: 'The Concordat',
    description: 'The economic engine. Guild halls, great markets, artisan quarters, and workshops.',
    vibe: 'Bustling commerce, guild rivalries, ambition, Renaissance intrigue.',
  },
  roots: {
    name: 'The Roots',
    faction: 'Contested',
    description: 'Where most adventures begin. The working poor, criminals, and those who refuse the order above.',
    vibe: 'Gritty, dangerous, vibrant, underground culture.',
  },
  deep: {
    name: 'The Deep',
    faction: 'The Ash Keepers',
    description: 'Ancient ruins from when magic was at its peak. Pre-Ebb architecture of impossible geometry.',
    vibe: 'Ancient horror, wonder, eldritch mystery, dungeon crawling.',
  },
} as const;

export const ADVENTURE_THEMES: Record<AdventureTheme, {
  name: string;
  description: string;
  icon: string;
  layerAffinity: Region[];
  gmToneDirective: string;
  questPreferences: string[];
}> = {
  dungeon_crawling: {
    name: 'Ruin Delvers',
    description: 'Descend into ancient pre-Ebb ruins. Recover Dead Logic artifacts, face traps and abominations in the crushing dark.',
    icon: 'Pickaxe',
    layerAffinity: ['deep'],
    gmToneDirective: 'Emphasize exploration, discovery, and the alien wonder of pre-Ebb architecture. Resource management matters — torches, rope, rations. Describe environments in visceral detail: dripping crystal veins, grinding dead constructs, impossible geometry. Treasure and artifacts are primary motivators. Combat is with ancient guardians, tunnel creatures, and corrupted constructs. Every room should hold something to discover or fear.',
    questPreferences: ['explore', 'fetch', 'investigate'],
  },
  underworld: {
    name: 'Underworld',
    description: 'Gang wars, smuggling rings, and criminal enterprise. Trust is currency and betrayal is always one deal away.',
    icon: 'Skull',
    layerAffinity: ['roots'],
    gmToneDirective: 'Emphasize moral gray areas, loyalty, and street-level survival. NPCs have ulterior motives — everyone is working an angle. Information is traded like currency. Violence is personal, not heroic. Describe grimy back alleys, underground fighting pits, smoke-filled dens, and hushed deals in candlelit backrooms. Combat involves gang enforcers, rival crews, and corrupt officials. The law is whatever the strongest gang says it is.',
    questPreferences: ['deliver', 'eliminate', 'investigate'],
  },
  city_guard: {
    name: 'City Watch',
    description: 'Protect Vannar\'s people. Investigate disturbances, defend against threats from below, and uphold justice in a city that tests it daily.',
    icon: 'Shield',
    layerAffinity: ['crown', 'span'],
    gmToneDirective: 'Emphasize duty, moral dilemmas, and the tension between law and justice. The player is an authority figure but faces situations where the rules fail. Threats escalate from petty crime to existential dangers. Describe guardhouses, patrol routes, crime scenes, and tense confrontations. Combat is against criminals, monsters threatening civilians, and occasionally corrupt officials. The player must choose between following orders and doing right.',
    questPreferences: ['investigate', 'eliminate', 'rescue', 'escort'],
  },
  intrigue: {
    name: 'Political Intrigue',
    description: 'Navigate the deadly power games between factions. Espionage, blackmail, and manipulation — in Vannar, words cut deeper than steel.',
    icon: 'Crown',
    layerAffinity: ['crown', 'span'],
    gmToneDirective: 'Emphasize social encounters, faction dynamics, and information warfare. Every NPC has an agenda and layers of motivation. Success comes from reading people and leveraging information. Describe opulent noble halls, hushed guild meetings, encrypted messages, and poisoned chalices. Combat is rare but dramatic — assassination, ambush, and betrayal. The real weapons are secrets, alliances, and leverage.',
    questPreferences: ['investigate', 'deliver', 'escort'],
  },
  mystery: {
    name: 'Mystery',
    description: 'Something is wrong in Vannar. Follow clues through the layers, interrogate witnesses, and piece together conspiracies that span the entire city.',
    icon: 'Search',
    layerAffinity: ['crown', 'span', 'roots', 'deep'],
    gmToneDirective: 'Emphasize investigation, clue discovery, and piecing together information. Every scene should contain something discoverable. Red herrings exist sparingly. Describe crime scenes, suspicious NPCs, hidden messages, and pieces of a larger puzzle. The player should feel like a detective in a fantasy noir. Combat is with those who want secrets buried. Maintain an atmosphere of creeping unease — something bigger is always lurking beneath the surface.',
    questPreferences: ['investigate', 'explore', 'rescue'],
  },
  monster_hunting: {
    name: 'Monster Hunters',
    description: 'Track and eliminate the dangerous creatures that crawl from The Deep. Study their weaknesses, prepare your tools, and bring down beasts that terrorize districts.',
    icon: 'Crosshair',
    layerAffinity: ['roots', 'deep'],
    gmToneDirective: 'Emphasize preparation, tracking, and the thrill of the hunt. Describe monster sign: claw marks, slime trails, terrified witnesses, half-eaten remains. The player should research weaknesses before engaging. Combat is climactic and against powerful creatures — fights where preparation determines survival. Describe visceral, dangerous encounters. Trophies, bounties, and creature lore are key rewards. Every hunt has phases: investigation, preparation, confrontation.',
    questPreferences: ['eliminate', 'investigate', 'explore'],
  },
} as const;

export const LANGUAGES: Record<GameLanguage, {
  name: string;
  englishName: string;
  flag: string;
}> = {
  en: {
    name: 'English',
    englishName: 'English',
    flag: '🇬🇧',
  },
  fi: {
    name: 'Suomi',
    englishName: 'Finnish',
    flag: '🇫🇮',
  },
} as const;
